import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import Topbar from "@/components/layout/Topbar";
import { StatusBadge } from "@/components/StatusBadge";
import { Webhook, Globe2, Plug } from "lucide-react";
import { toast } from "sonner";

const CATEGORY_META = {
    payment: { title: "Payment Gateways", subtitle: "Card, wallet & alternative payment methods", icon: Webhook },
    ota: { title: "OTA Integrations", subtitle: "Online travel agencies & distribution channels", icon: Globe2 },
    platform: { title: "Platform Engines", subtitle: "Channel Manager & Booking Engine", icon: Plug },
};

export function IntegrationsPage({ category }) {
    const meta = CATEGORY_META[category];
    const [items, setItems] = useState([]);
    const [testing, setTesting] = useState(null);

    const load = async () => {
        const { data } = await api.get(`/integrations?category=${category}`);
        setItems(data);
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [category]);

    const toggle = async (it) => {
        try {
            await api.patch(`/integrations/${it.id}`, {
                is_enabled: !it.is_enabled,
                status: !it.is_enabled ? "connected" : "disconnected",
            });
            await load();
        } catch { toast.error("Failed"); }
    };
    const test = async (it) => {
        setTesting(it.id);
        try {
            const { data } = await api.post(`/integrations/${it.id}/test`);
            toast.success(`${data.provider} reachable · ${data.latency_ms}ms`);
        } catch { toast.error("Test failed"); }
        finally { setTesting(null); }
    };

    return (
        <>
            <Topbar title={meta.title} subtitle={meta.subtitle} />
            <div className="flex-1 overflow-auto p-6">
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
                    {items.map((it) => (
                        <div key={it.id} className="akm-card p-4" data-testid={`integration-${it.provider.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}>
                            <div className="flex items-start justify-between">
                                <div className="flex items-center gap-2">
                                    <div className="flex h-8 w-8 items-center justify-center rounded-md bg-muted text-foreground">
                                        <meta.icon size={14} />
                                    </div>
                                    <div>
                                        <div className="text-sm font-semibold tracking-tight">{it.provider}</div>
                                        <div className="text-[11px] text-muted-foreground">{it.description}</div>
                                    </div>
                                </div>
                                <StatusBadge status={it.status === "connected" ? "active" : it.status === "error" ? "error" : "skipped"} label={it.status} />
                            </div>
                            {it.api_key_hint && (
                                <div className="mt-3 font-mono text-[11px] text-muted-foreground">Key: {it.api_key_hint}</div>
                            )}
                            <div className="mt-3 flex items-center gap-2">
                                <button onClick={() => toggle(it)} className="akm-btn-ghost flex-1 text-[12px]" data-testid={`toggle-${it.provider.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}>
                                    {it.is_enabled ? "Disable" : "Enable"}
                                </button>
                                <button onClick={() => test(it)} disabled={testing === it.id} className="akm-btn-ghost text-[12px]">{testing === it.id ? "Testing…" : "Test"}</button>
                            </div>
                        </div>
                    ))}
                </div>
                {items.length === 0 && <div className="py-12 text-center text-sm text-muted-foreground">No integrations in this category</div>}
            </div>
        </>
    );
}

export const SuperPaymentGateways = () => <IntegrationsPage category="payment" />;
export const SuperOTAs = () => <IntegrationsPage category="ota" />;
